/**
 * A small, self-contained markdown renderer for transcript text blocks. Covers
 * the cases agent output actually uses: headings, paragraphs (with soft breaks),
 * fenced code blocks, unordered/ordered lists, and inline **bold** / *italic* /
 * `code` / [links](url) / bare urls, plus GFM tables. Deliberately
 * dependency-free (no artifact coupling, no markdown lib) to keep
 * workspace-next self-contained; upgrade to a real GFM lib if we ever need
 * footnotes or nested structure.
 */

import { createContext, Fragment, type ReactNode, useContext } from 'react'

/**
 * Open a thing in the project by id: what a link with no scheme points at.
 *
 * The composer's `@` writes `[title](<id>)` (RefPicker), the same shape a
 * document's own links take. As an `<a href>` a bare id resolves RELATIVE to
 * the window's url and navigates the window away, so a link that is not a url
 * is handed here instead. Absent, it draws as plain text: saying what it names
 * without offering to go there beats a link that breaks the page.
 */
export const OpenRef = createContext<((id: string) => void) | null>(null)

/**
 * An id, and nothing else: letters, digits, `-` and `_`. Anything with a
 * scheme, a slash or a dot is a url, which keeps `www.example.com` a link to
 * the web rather than a document lookup that finds nothing.
 */
const REF_ID = /^[A-Za-z0-9_-]+$/

/** A url written without its scheme (`www.example.com`) gets one, or the
 *  browser resolves it against the window's own address. */
function hrefFor(target: string): string {
  return /^([a-z][a-z0-9+.-]*:|\/|#)/i.test(target)
    ? target
    : `https://${target}`
}

function RefLink({ id, children }: { id: string; children: ReactNode }) {
  const open = useContext(OpenRef)
  if (!open) return <span className="text-blue-400">{children}</span>
  return (
    <button
      type="button"
      onClick={() => open(id)}
      className="text-blue-400 hover:underline"
    >
      {children}
    </button>
  )
}

// ---- inline ----------------------------------------------------------------

interface InlineRule {
  re: RegExp
  render: (m: RegExpExecArray, key: string) => ReactNode
  /** If true, the captured group is literal (not re-parsed) — for code spans. */
  literal?: boolean
}

const INLINE_RULES: InlineRule[] = [
  {
    re: /`([^`]+)`/,
    literal: true,
    render: (m, key) => (
      <code
        key={key}
        // break-all, not break-words: an inline code span is often ONE long
        // token with no break opportunity (a path, an id, a call like
        // typeApi().create(...)), and without this it pushes the whole thread
        // wider than the panel and adds a horizontal scrollbar.
        className="break-all rounded bg-black/30 px-1 py-0.5 font-mono text-[0.85em]"
      >
        {m[1]}
      </code>
    ),
  },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (m, key) =>
      REF_ID.test(m[2]) ? (
        <RefLink key={key} id={m[2]}>
          {parseInline(m[1], `${key}i`)}
        </RefLink>
      ) : (
        <a
          key={key}
          href={hrefFor(m[2])}
          target="_blank"
          rel="noreferrer noopener"
          className="text-blue-400 hover:underline"
        >
          {parseInline(m[1], `${key}i`)}
        </a>
      ),
  },
  {
    re: /\*\*([^*]+)\*\*/,
    render: (m, key) => (
      <strong key={key}>{parseInline(m[1], `${key}i`)}</strong>
    ),
  },
  {
    re: /(?:\*([^*\n]+)\*|_([^_\n]+)_)/,
    render: (m, key) => (
      <em key={key}>{parseInline(m[1] ?? m[2], `${key}i`)}</em>
    ),
  },
  {
    re: /(https?:\/\/[^\s)]+)/,
    render: (m, key) => (
      <a
        key={key}
        href={m[1]}
        target="_blank"
        rel="noreferrer noopener"
        // A bare url is one long token by construction, so it is the most
        // reliable way to widen the thread.
        className="break-all text-blue-400 hover:underline"
      >
        {m[1]}
      </a>
    ),
  },
]

function parseInline(text: string, keyBase = ''): ReactNode[] {
  let earliest: { m: RegExpExecArray; rule: InlineRule } | null = null
  for (const rule of INLINE_RULES) {
    const m = new RegExp(rule.re).exec(text)
    if (m && (earliest === null || m.index < earliest.m.index)) {
      earliest = { m, rule }
    }
  }
  if (!earliest) return text ? [text] : []
  const { m, rule } = earliest
  const before = text.slice(0, m.index)
  const after = text.slice(m.index + m[0].length)
  return [
    ...(before ? [before] : []),
    rule.render(m, `${keyBase}m${m.index}`),
    ...parseInline(after, `${keyBase}a`),
  ]
}

// ---- blocks ----------------------------------------------------------------

function isListItem(line: string): { ordered: boolean; text: string } | null {
  const ul = /^\s*[-*]\s+(.*)$/.exec(line)
  if (ul) return { ordered: false, text: ul[1] }
  const ol = /^\s*\d+\.\s+(.*)$/.exec(line)
  if (ol) return { ordered: true, text: ol[1] }
  return null
}

// ---- tables ----------------------------------------------------------------

type Align = 'left' | 'center' | 'right' | null

/**
 * Split a table row on its cell pipes. A pipe does NOT separate cells when it
 * is escaped (`\|`) or inside a code span, which is the common case in this
 * app: agent output routinely puts a shell pipeline or a union type in a cell.
 */
function splitRow(line: string): string[] {
  const cells: string[] = []
  let cur = ''
  let inCode = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '\\' && line[i + 1] === '|') {
      cur += '|'
      i++
      continue
    }
    if (ch === '`') inCode = !inCode
    if (ch === '|' && !inCode) {
      cells.push(cur)
      cur = ''
      continue
    }
    cur += ch
  }
  cells.push(cur)
  // A row is normally fenced by leading and trailing pipes, which produce an
  // empty cell at each end. Both are optional in GFM, so drop them only when
  // they are actually empty.
  if (cells.length && cells[0].trim() === '') cells.shift()
  if (cells.length && cells[cells.length - 1].trim() === '') cells.pop()
  return cells.map((c) => c.trim())
}

/** `---`, `:--`, `--:`, `:-:`, with optional spaces. */
function parseAlign(cell: string): Align | undefined {
  const m = /^(:?)-+(:?)$/.exec(cell.trim())
  if (!m) return undefined
  if (m[1] && m[2]) return 'center'
  if (m[2]) return 'right'
  if (m[1]) return 'left'
  return null
}

/**
 * A table starts where a header line is followed by a delimiter line with the
 * same number of cells. Requiring the counts to match is what keeps ordinary
 * prose containing a pipe from being swallowed as a table.
 */
function tableAt(lines: string[], i: number): { align: Align[] } | null {
  if (i + 1 >= lines.length) return null
  if (!lines[i].includes('|')) return null
  if (!lines[i + 1].includes('|')) return null
  const header = splitRow(lines[i])
  const delim = splitRow(lines[i + 1])
  if (header.length === 0 || header.length !== delim.length) return null
  const align: Align[] = []
  for (const cell of delim) {
    const a = parseAlign(cell)
    if (a === undefined) return null
    align.push(a)
  }
  return { align }
}

const ALIGN_CLASS: Record<Exclude<Align, null>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

function cellClass(align: Align): string {
  return align ? ` ${ALIGN_CLASS[align]}` : ''
}

export function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]

    // fenced code block
    const fence = /^```(\w*)\s*$/.exec(line)
    if (fence) {
      const code: string[] = []
      i++
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        code.push(lines[i])
        i++
      }
      i++ // closing fence
      blocks.push(
        <pre
          key={key++}
          className="my-2 overflow-x-auto rounded-md bg-black/40 p-3 font-mono text-[0.82em] leading-relaxed"
        >
          <code>{code.join('\n')}</code>
        </pre>,
      )
      continue
    }

    // heading
    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      const level = heading[1].length
      const sizes = [
        'text-lg',
        'text-base',
        'text-sm',
        'text-sm',
        'text-sm',
        'text-sm',
      ]
      blocks.push(
        <p
          key={key++}
          className={`mt-3 mb-1 font-semibold ${sizes[level - 1]}`}
        >
          {parseInline(heading[2], `h${key}`)}
        </p>,
      )
      i++
      continue
    }

    // list (consecutive items)
    if (isListItem(line)) {
      const items: ReactNode[] = []
      const ordered = isListItem(line)?.ordered ?? false
      let n = 0
      while (i < lines.length) {
        const item = isListItem(lines[i])
        if (!item) break
        items.push(
          <li
            key={n++}
            className="ml-5 list-outside list-disc break-words marker:text-muted-foreground"
          >
            {parseInline(item.text, `li${key}-${n}`)}
          </li>,
        )
        i++
      }
      blocks.push(
        ordered ? (
          <ol key={key++} className="my-1 list-decimal space-y-0.5">
            {items}
          </ol>
        ) : (
          <ul key={key++} className="my-1 space-y-0.5">
            {items}
          </ul>
        ),
      )
      continue
    }

    // GFM table
    const table = tableAt(lines, i)
    if (table) {
      const head = splitRow(lines[i])
      i += 2 // header + delimiter
      const rows: string[][] = []
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        lines[i].includes('|')
      ) {
        rows.push(splitRow(lines[i]))
        i++
      }
      const tk = key++
      blocks.push(
        // The panel is narrow and a table is the one block whose width is set
        // by its content, so it scrolls itself rather than widening the thread.
        <div key={tk} className="my-2 overflow-x-auto">
          <table className="w-full border-collapse text-[0.92em]">
            <thead>
              <tr className="border-border border-b-2">
                {head.map((cell, c) => (
                  <th
                    // biome-ignore lint/suspicious/noArrayIndexKey: fixed split of one row; never reordered
                    key={c}
                    className={`border-border border px-2 py-1 font-semibold${cellClass(table.align[c] ?? null)}`}
                  >
                    {parseInline(cell, `th${tk}-${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed split of the table's lines; never reordered
                <tr key={r}>
                  {/* A short row is padded, not dropped: a cell missing from the
                      source should leave a gap, not shift the columns left. */}
                  {head.map((_, c) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: column index is the identity here
                      key={c}
                      className={`border-border border px-2 py-1 align-top${cellClass(table.align[c] ?? null)}`}
                    >
                      {parseInline(row[c] ?? '', `td${tk}-${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // blank line
    if (line.trim() === '') {
      i++
      continue
    }

    // paragraph (consecutive non-blank, non-special lines, soft breaks preserved)
    const para: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^```/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !isListItem(lines[i]) &&
      // A table may follow a paragraph with no blank line between them.
      !tableAt(lines, i)
    ) {
      para.push(lines[i])
      i++
    }
    blocks.push(
      <p
        key={key++}
        // whitespace-pre-wrap preserves newlines but only wraps AT whitespace,
        // so a long unbroken run (a url, a hash, a path) overflows. break-words
        // lets it break as a last resort while leaving normal prose alone.
        className="my-1 break-words whitespace-pre-wrap leading-relaxed"
      >
        {para.map((pl, idx) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static split of one paragraph's lines; never reordered
          <Fragment key={idx}>
            {idx > 0 && <br />}
            {parseInline(pl, `p${key}-${idx}`)}
          </Fragment>
        ))}
      </p>,
    )
  }

  return <div className="text-sm">{blocks}</div>
}
